import { useState, useEffect } from 'react'
import { useAuth } from '../lib/AuthContext'
import { useProfile, useAllLogs, useInventory, useMealTemplates, useAiPlan } from '../lib/hooks'
import { notificationsSupported, requestNotificationPermission } from '../lib/notifications'
import { exportFullBackup, exportDailySummaryCSV } from '../lib/exportData'
import { generateAndSaveAiPlan } from '../lib/aiPlan'
import { adjustAiPlan } from '../lib/planChat'
import { PLAN_START_DATE } from '../data/plan'

export default function Settings() {
  const { user, logout } = useAuth()
  const { profile, setStartDate, updateProfile } = useProfile()
  const { logs } = useAllLogs()
  const { inventory } = useInventory()
  const { templates } = useMealTemplates()
  const { aiPlan } = useAiPlan()
  const [date, setDate] = useState(profile?.startDate || PLAN_START_DATE)
  const [targetWeight, setTargetWeight] = useState('')
  const [alertsOn, setAlertsOn] = useState(() => localStorage.getItem('alertsEnabled') === 'true')
  const [permMsg, setPermMsg] = useState('')
  const [saved, setSaved] = useState(false)
  const [targetSaved, setTargetSaved] = useState(false)
  const [experience, setExperience] = useState('')
  const [goesToGym, setGoesToGym] = useState('')
  const [equipment, setEquipment] = useState('')
  const [dietaryPreferences, setDietaryPreferences] = useState('')
  const [injuries, setInjuries] = useState('')
  const [planBusy, setPlanBusy] = useState(false)
  const [planError, setPlanError] = useState('')
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [chatBusy, setChatBusy] = useState(false)

  useEffect(() => {
    if (profile?.startDate) setDate(profile.startDate)
  }, [profile?.startDate])

  useEffect(() => {
    if (profile?.targetWeightKg != null) setTargetWeight(String(profile.targetWeightKg))
  }, [profile?.targetWeightKg])

  const saveDate = () => {
    if (!date) return
    setStartDate(date)
    setSaved(true)
  }

  const saveTargetWeight = () => {
    const val = parseFloat(targetWeight)
    updateProfile({ targetWeightKg: Number.isFinite(val) ? val : null })
    setTargetSaved(true)
  }

  const generatePlan = async () => {
    setPlanError('')
    const ageNum = profile?.age
    const heightNum = profile?.heightCm
    const weightNum = profile?.weightKg
    if (!Number.isFinite(ageNum) || !Number.isFinite(heightNum) || !Number.isFinite(weightNum)) {
      setPlanError('Set your age, height and weight above first.')
      return
    }
    setPlanBusy(true)
    try {
      const genProfile = {
        age: ageNum,
        gender: profile?.gender,
        heightCm: heightNum,
        weightKg: weightNum,
        targetWeightKg: profile?.targetWeightKg,
        experience: experience || undefined,
        goesToGym: goesToGym === 'yes',
        equipment: goesToGym === 'no' ? equipment.trim() : undefined,
        dietaryPreferences: dietaryPreferences.trim() || undefined,
        injuries: injuries.trim() || undefined,
      }
      await updateProfile({
        experience: experience || null,
        goesToGym: goesToGym ? goesToGym === 'yes' : null,
        equipment: goesToGym === 'no' ? equipment.trim() : null,
        dietaryPreferences: dietaryPreferences.trim() || null,
        injuries: injuries.trim() || null,
      })
      await generateAndSaveAiPlan(user.uid, genProfile)
    } catch {
      setPlanError('Could not generate your plan right now - try again in a bit.')
    } finally {
      setPlanBusy(false)
    }
  }

  const sendChatMessage = async (e) => {
    e.preventDefault()
    const text = chatInput.trim()
    if (!text || chatBusy) return
    setChatMessages((prev) => [...prev, { role: 'user', text }])
    setChatInput('')
    setChatBusy(true)
    try {
      const summary = await adjustAiPlan(user.uid, aiPlan, text, profile)
      setChatMessages((prev) => [...prev, { role: 'assistant', text: summary }])
    } catch {
      setChatMessages((prev) => [...prev, { role: 'assistant', text: "Couldn't apply that change - try again in a bit.", isError: true }])
    } finally {
      setChatBusy(false)
    }
  }

  const toggleAlerts = async () => {
    if (!alertsOn) {
      const perm = await requestNotificationPermission()
      if (perm !== 'granted') {
        setPermMsg('Notifications were blocked. Enable them for this app in iOS Settings > Notifications.')
        return
      }
    }
    const next = !alertsOn
    setAlertsOn(next)
    localStorage.setItem('alertsEnabled', String(next))
    setPermMsg('')
  }

  const initial = (user?.email || '?').charAt(0).toUpperCase()

  return (
    <div className="section-narrow">
      <h1 className="display-heading" style={{ fontSize: 'clamp(30px, 8vw, 72px)', marginBottom: 20 }}>Settings</h1>

      <div className="settings-card settings-account-row">
        <div className="settings-account-left">
          <div className="settings-avatar">{initial}</div>
          <div className="settings-account-meta">
            <span className="settings-label">ACCOUNT</span>
            <span className="settings-email">{user?.email}</span>
          </div>
        </div>
        <button className="btn btn-ghost" onClick={logout}>Log out</button>
      </div>

      <div className="settings-card">
        <span className="settings-label">PLAN START DATE</span>
        <p className="settings-desc">Day 1 is anchored to this date. Changing it shifts which day the app shows as "Today".</p>
        <div className="settings-row">
          <input
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value)
              setSaved(false)
            }}
          />
          <button className="settings-save-btn" onClick={saveDate}>{saved ? 'SAVED ✓' : 'SAVE'}</button>
        </div>
      </div>

      <div className="settings-card">
        <span className="settings-label">TARGET WEIGHT</span>
        <p className="settings-desc">Used for the goal projection on the Progress page. Leave blank to hide it.</p>
        <div className="settings-row">
          <input
            type="number"
            inputMode="decimal"
            placeholder="e.g. 75"
            value={targetWeight}
            onChange={(e) => {
              setTargetWeight(e.target.value)
              setTargetSaved(false)
            }}
          />
          <button className="settings-save-btn" onClick={saveTargetWeight}>{targetSaved ? 'SAVED ✓' : 'SAVE'}</button>
        </div>
      </div>

      <div className="settings-card">
        <span className="settings-label">REMINDERS</span>
        {notificationsSupported() ? (
          <>
            <div className="switch-row">
              <span className="switch-title">Meal & supplement alerts</span>
              <button className={`switch ${alertsOn ? 'is-on' : ''}`} onClick={toggleAlerts} type="button" aria-pressed={alertsOn}>
                <span className="switch-knob" />
              </button>
            </div>
            <p className="settings-desc">
              Works while the app is open on your phone around meal/supplement time. iOS does not allow background alerts for
              home-screen web apps without a push server, so keep the app open (or glance at it) near your meal times for the
              most reliable reminder.
            </p>
            {permMsg && <p className="error-text">{permMsg}</p>}
          </>
        ) : (
          <p className="settings-desc">Notifications aren't supported in this browser. On iPhone, add this app to your Home Screen from Safari first (Share → Add to Home Screen), then open it from the home screen icon.</p>
        )}
      </div>

      <div className="settings-card">
        <span className="settings-label">AI PLAN</span>
        {aiPlan ? (
          <p className="settings-desc">
            Your personalized 90-day plan was generated on {new Date(aiPlan.generatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.
          </p>
        ) : (
          <>
            <p className="settings-desc">Generate a personalized workout &amp; meal plan based on your body, goals, equipment and experience - a one-time generation using your profile above (set age/height/weight first).</p>
            <div className="login-field-row">
              <label>
                Experience level
                <select value={experience} onChange={(e) => setExperience(e.target.value)}>
                  <option value="">Skip</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </label>
              <label>
                Gym access?
                <select value={goesToGym} onChange={(e) => setGoesToGym(e.target.value)}>
                  <option value="">Skip</option>
                  <option value="yes">Yes, I go to a gym</option>
                  <option value="no">No, home only</option>
                </select>
              </label>
            </div>
            {goesToGym === 'no' && (
              <label>
                Equipment you have at home
                <input type="text" placeholder="e.g. dumbbells, resistance bands (leave blank for bodyweight only)" value={equipment} onChange={(e) => setEquipment(e.target.value)} />
              </label>
            )}
            <label>
              Dietary preferences / restrictions
              <input type="text" placeholder="e.g. vegetarian, no nuts" value={dietaryPreferences} onChange={(e) => setDietaryPreferences(e.target.value)} />
            </label>
            <label>
              Injuries / limitations
              <input type="text" placeholder="e.g. lower back issue, avoid overhead pressing" value={injuries} onChange={(e) => setInjuries(e.target.value)} />
            </label>
            {planError && <p className="error-text">{planError}</p>}
            <button className="btn btn-primary full-width" onClick={generatePlan} disabled={planBusy} type="button" style={{ marginTop: 8 }}>
              {planBusy ? 'Generating your plan…' : 'Generate My AI Plan'}
            </button>
          </>
        )}
      </div>

      {aiPlan && (
        <div className="settings-card">
          <span className="settings-label">FINE-TUNE YOUR PLAN</span>
          <p className="settings-desc">
            Ask for a change in plain English and the AI will edit your existing plan - e.g. "swap bench press for push-ups
            on Push day", "make Tuesdays vegetarian", or "increase all my squat weights by 5kg". Everything else stays untouched.
          </p>
          {chatMessages.length > 0 && (
            <div className="plan-chat-log">
              {chatMessages.map((m, i) => (
                <div key={i} className={`plan-chat-msg plan-chat-msg-${m.role}${m.isError ? ' is-error' : ''}`}>
                  {m.text}
                </div>
              ))}
              {chatBusy && <div className="plan-chat-msg plan-chat-msg-assistant plan-chat-msg-pending">Thinking…</div>}
            </div>
          )}
          <form onSubmit={sendChatMessage} className="plan-chat-form">
            <input
              type="text"
              placeholder="e.g. swap bench press for push-ups on Push day"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              disabled={chatBusy}
            />
            <button className="btn btn-primary" type="submit" disabled={chatBusy || !chatInput.trim()}>
              {chatBusy ? '…' : 'SEND'}
            </button>
          </form>
        </div>
      )}

      <div className="settings-card">
        <span className="settings-label">EXPORT DATA</span>
        <p className="settings-desc">A personal backup independent of the app, and portable if you ever want to look at it elsewhere.</p>
        <div className="settings-row">
          <button className="btn btn-ghost" onClick={() => exportFullBackup(profile, logs, inventory, templates)} type="button">
            FULL BACKUP (.JSON)
          </button>
          <button className="btn btn-ghost" onClick={() => exportDailySummaryCSV(logs, aiPlan)} type="button">
            DAILY SUMMARY (.CSV)
          </button>
        </div>
      </div>
    </div>
  )
}
